import { useEffect, useState } from "react";
import PlayerList from "components/onboarding/_localMultiplayer/PlayerList";
import AddPlayerModal from "components/onboarding/_localMultiplayer/AddPlayerModal";
import { createRoom } from "services/rooms";
import AvatarStep from "components/onboarding/_steps/AvatarStep";
import { useDispatch } from "react-redux";
import { setRoomReducer } from "../../redux/room/actions";
import EmailStepUnwrapped from "./_steps/EmailStepUnwrapped";
import AvatarAndPseudoStepUnwraped from "./_steps/AvatarAndPseudoStepUnwraped";
import PseudoStepUnwrapped from "./_steps/PseudoStepUnwrapped";
import ValidationStepUnwrapped from "./_steps/ValidationStepUnwrapped";

export default function LocalMultiplayerOnboarding({
	quiz,
	quizWrapper,
	navigate,
	room,
	setRoom,
}) {
	const [players, setPlayers] = useState([]);
	const [roomName, setRoomName] = useState("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [currentPlayer, setCurrentPlayer] = useState(null);
	const [casesAmount, setCasesAmount] = useState(20);
	const [playerValidated, setPlayerValidated] = useState(false);
	const [avatarChangeRequested, setAvatarChangeRequested] = useState(false);
	const [playerChangeRequested, setPlayerChangeRequested] = useState(false);

	const dispatch = useDispatch();

	useEffect(() => {
		// Generate board cases
		let cases = Array.from({ length: casesAmount }, (_, index) => {
			if (index % 5 === 0 && index !== 0) {
				return { type: "bonus" };
			}
			return { type: "question" };
		});

		setRoom((prevRoom) => ({
			...prevRoom,
			quiz_id: quiz.id,
			name: roomName,
			players: players,
			board_cases: cases,
			current_player_index: 0,
		}));
	}, [players, casesAmount, roomName]);

	const openPlayerModal = () => {
		setCurrentPlayer({});
		setIsModalOpen(true);
	};

	const handleStepCompletion = (data) => {
		const updatedPlayer = { ...currentPlayer, ...data };

		if (
			(quiz.auth_steps.includes("email") && updatedPlayer?.email) ||
			(quiz.auth_steps.includes("pseudo") && updatedPlayer?.pseudo) ||
			(quiz.auth_steps.includes("avatar") && updatedPlayer?.avatar)
		) {
			setCurrentPlayer(updatedPlayer);
		} else {
			handleValidatePlayer({ ...updatedPlayer, position: 0, score: 0 });
		}
	};

	const handleValidatePlayer = (player) => {
		setPlayers([...players, player]);
		setIsModalOpen(false);
		setCurrentPlayer(null);
	};

	const handlePlayerValidation = () => {
		handleValidatePlayer(currentPlayer);
	};

	const steps = [
		{
			condition: (p) =>
				quiz.auth_steps.includes("email") &&
				(!p?.email || playerChangeRequested),
			component: (
				<EmailStepUnwrapped
					quizOrganizationId={quizWrapper?.organization_id}
					key="email"
					onEmailChosen={(email) => handleStepCompletion({ email })}
				/>
			),
		},
		{
			condition: (p) =>
				quiz.auth_steps.includes("avatar_and_pseudo") &&
				(!p?.pseudo || !p?.avatar),
			component: (
				<AvatarAndPseudoStepUnwraped
					quizOrganizationId={quizWrapper?.organization_id}
					key="avatar-and-pseudo"
					player={currentPlayer}
					onComplete={(data) => handleStepCompletion(data)}
				/>
			),
		},
		{
			condition: (p) => quiz.auth_steps.includes("pseudo") && !p?.pseudo,
			component: (
				<PseudoStepUnwrapped
					quizOrganizationId={quizWrapper?.organization_id}
					key="pseudo"
					onPseudoChosen={(pseudo) => handleStepCompletion({ pseudo })}
				/>
			),
		},
		{
			condition: (p) =>
				(quiz.auth_steps.includes("avatar") && !p?.avatar) ||
				avatarChangeRequested,
			component: (
				<AvatarStep
					quizOrganizationId={quizWrapper?.organization_id}
					key="avatar"
					onAvatarChosen={(avatar) => handleStepCompletion({ avatar })}
				/>
			),
		},
		{
			condition: (p) =>
				quiz.auth_steps.includes("validation") && !playerValidated,
			component: (
				<ValidationStepUnwrapped
					key="validation"
					player={currentPlayer}
					quizOrganizationId={quizWrapper?.organization_id}
					onValidatePlayer={handlePlayerValidation}
					onChangePlayer={() => setPlayerChangeRequested(true)}
					onEditAvatar={() => setAvatarChangeRequested(true)}
				/>
			),
		},
	];

	const currentStep = steps.find((step) => step.condition(currentPlayer));

	const handleCreateRoom = async () => {
		if (!roomName || players.length === 0) {
			alert("Veuillez entrer un nom de salle et ajouter au moins un joueur.");
			return;
		}

		const { data: _room } = await createRoom(room);

		if (_room) {
			localStorage.setItem(
				"room",
				JSON.stringify({ ...room, id: _room.id, ..._room })
			);
			dispatch(setRoomReducer({ ...room, id: _room.id, ..._room }));
			navigate(`/play/r/${_room.id}`);
		}
	};

	return (
		<>
			<div className="flex flex-col z-10 items-center w-full h-full mt-16 min-w-80 max-w-80 sm:min-w-96 sm:max-w-96 bg-white p-6 rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,0.3)] ">
				<h1 className="text-2xl text-gray-600 font-bold text-center mb-8">
					Créer une salle
				</h1>
				<div className="flex flex-col gap-2 items-start w-full">
					<p className="text-md">Choisissez un nom :</p>
					<input
						type="text"
						placeholder="Nom de la salle"
						className="border p-2 rounded mb-4 w-full"
						value={roomName}
						onChange={(e) => setRoomName(e.target.value)}
					/>
				</div>

				<div className="flex flex-col gap-2 items-start w-full">
					<p className="text-md">
						{"Choisissez le nombre de questions: (max. 82)"}
					</p>
					<input
						type="number"
						placeholder="Nom de la salle"
						className="border p-2 rounded mb-4 w-full"
						value={casesAmount}
						onChange={(e) => setCasesAmount(Math.min(e.target.value, 82))}
					/>
				</div>

				<PlayerList players={players} />
				<div className="flex flex-col gap-2 items-center mt-8">
					<button
						onClick={openPlayerModal}
						className="mt-2 rounded-full px-4 py-2 border-black text-white bg-[#adadad] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#9d9d9d] disabled:bg-slate-300"
					>
						+ Ajouter un joueur
					</button>
					<button
						onClick={handleCreateRoom}
						className="mt-2 rounded-full px-4 py-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256] disabled:bg-slate-300"
					>
						Commencer la partie
					</button>
				</div>
			</div>
			<AddPlayerModal
				isOpen={isModalOpen}
				closeModal={() => setIsModalOpen(false)}
			>
				{currentStep?.component}
			</AddPlayerModal>
		</>
	);
}
